import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { 
  Barbell, 
  ChartLineUp, 
  BowlFood, 
  UsersFour,
  WhatsappLogo,
  ArrowRight,
  Lightning
} from "@phosphor-icons/react";

// ============ CONFIGURATION ============
const WHATSAPP_NUMBER = "34641099731";
const WHATSAPP_MESSAGE = "Hola Jordi, quiero información sobre el acompañamiento premium GOATGYM";

// Premium Images - REAL founder photos + generated assets
const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/ad525726-9ac2-45ae-8c95-64b3bbaa5d43/images/22bd503f8101b02d8d0c7b2abed41d8368cf08e374802dac123ce8af5bd292e2.png",
  motivational: "https://static.prod-images.emergentagent.com/jobs/ad525726-9ac2-45ae-8c95-64b3bbaa5d43/images/56636c5a0c94ac41bab141d404b5c8f0f3f0f3381c5d1150a0d9e7fcc68b3b4e.png",
  // REAL founder image - Entrenamientos Guiados
  founder: "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/6lpkvb4m_0F8F7D0A-2BAA-4BEF-9903-E8B033455BB0.jpeg",
  // REAL founder gym selfie - Seguimiento Real
  tracking: "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/5d8ko8in_00083195-1271-40DA-B5F6-3FCB66958CC5.png",
  mealPrep: "https://static.prod-images.emergentagent.com/jobs/ad525726-9ac2-45ae-8c95-64b3bbaa5d43/images/9ad163505bddf4e4ec626d46a19d1954c7c529ae0dfae23746c95c7a4d3f3570.png",
  // REAL founder coaching pose - Coaching Premium
  coaching: "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/ecattype_F5717EB5-1B0D-4A1F-A1CB-23D101548C17.png"
};

// ============ NAVBAR ============
const Navbar = ({ onAccess }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? "backdrop-blur-xl bg-[#0A0A0A]/95 border-b border-white/5" 
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-3 sm:py-4 flex items-center justify-between">
        <motion.div className="flex items-center gap-2" whileHover={{ scale: 1.02 }}>
          <span className="font-['Outfit'] text-xl sm:text-2xl font-black tracking-tighter text-white">
            GOAT<span className="text-[#D4AF37]">GYM</span>
          </span>
        </motion.div>

        <motion.button
          onClick={onAccess}
          whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(212, 175, 55, 0.4)" }}
          whileTap={{ scale: 0.98 }}
          className="px-4 sm:px-6 py-2 sm:py-2.5 bg-[#D4AF37] text-black font-bold text-xs sm:text-sm tracking-[0.08em] uppercase hover:bg-[#F0C74A] transition-all duration-300"
          data-testid="nav-acceder-btn"
        >
          Acceder
        </motion.button>
      </div>
    </motion.nav>
  );
};

// ============ HERO SECTION ============
const HeroSection = ({ onAccess, onWhatsApp }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background Image with Parallax */}
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img 
          src={IMAGES.hero}
          alt="Premium gym"
          className="w-full h-full object-cover scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/60 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
      </motion.div>

      {/* Content */}
      <motion.div 
        style={{ opacity }}
        className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 pt-24 sm:pt-32 pb-16 sm:pb-20"
      >
        <div className="max-w-3xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6 sm:mb-8"
          >
            <Lightning weight="fill" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs sm:text-sm font-bold tracking-wider uppercase">
              Greatest Of All Times
            </span>
          </motion.div>

          {/* Main Headline - MOBILE OPTIMIZED */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-['Outfit'] text-[2.5rem] leading-[0.95] sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight uppercase text-white mb-5 sm:mb-6"
          >
            <span className="block">No es solo</span>
            <span className="block">un gimnasio.</span>
            <span className="block text-[#D4AF37] mt-1">Es una</span>
            <span className="block text-[#D4AF37]">transformación.</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-base sm:text-lg md:text-xl text-[#A3A3A3] leading-relaxed max-w-xl mb-8 sm:mb-10"
          >
            Entrenamiento, disciplina, nutrición y seguimiento real.
            <span className="block text-white font-medium mt-2">
              Diseñado para quienes quieren llevar su físico y mentalidad al siguiente nivel.
            </span>
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            {/* Primary CTA - Premium Button */}
            <motion.button
              onClick={onAccess}
              whileHover={{ 
                scale: 1.02, 
                boxShadow: "0 0 50px rgba(212, 175, 55, 0.4), inset 0 0 20px rgba(255,255,255,0.1)" 
              }}
              whileTap={{ scale: 0.98 }}
              className="group relative px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-black font-bold text-sm tracking-[0.1em] uppercase overflow-hidden transition-all duration-500 flex items-center justify-center gap-3"
              data-testid="hero-acceder-btn"
            >
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative">Acceder a la app</span>
              <ArrowRight weight="bold" className="relative w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            {/* Secondary CTA */}
            <motion.button
              onClick={onWhatsApp}
              whileHover={{ scale: 1.02, backgroundColor: "rgba(212, 175, 55, 0.1)" }}
              whileTap={{ scale: 0.98 }}
              className="group px-6 sm:px-8 py-3.5 sm:py-4 border border-[#D4AF37]/60 text-[#D4AF37] font-bold text-sm tracking-[0.1em] uppercase hover:border-[#D4AF37] transition-all duration-300 flex items-center justify-center gap-3"
              data-testid="hero-whatsapp-btn"
            >
              <WhatsappLogo weight="fill" className="w-5 h-5" />
              <span>Hablar con entrenador</span>
            </motion.button>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator - Hidden on very small screens */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-10 hidden sm:block"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-5 h-8 border-2 border-white/30 rounded-full flex items-start justify-center p-1.5"
        >
          <motion.div className="w-1 h-1 bg-[#D4AF37] rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============ BENEFITS SECTION ============
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Barbell,
      title: "Entrenamientos Guiados",
      description: "Programas desde principiante hasta nivel GOAT. Adaptados a tu evolución.",
      tags: ["Principiante", "Intermedio", "Avanzado", "GOAT"],
      span: "col-span-1 md:col-span-2 lg:row-span-2",
      image: IMAGES.founder,
      featured: true,
      imagePosition: "object-[center_35%]", // Better face centering for tall card
      imageOpacity: "opacity-80"
    },
    {
      icon: ChartLineUp,
      title: "Seguimiento Real",
      description: "Métricas de progreso, check-ins y evolución. Datos que impulsan resultados.",
      tags: ["Progreso", "Métricas", "Check-ins"],
      span: "col-span-1",
      image: IMAGES.tracking,
      imagePosition: "object-[center_5%]", // Top position to show full face on small screens
      imageOpacity: "opacity-60"
    },
    {
      icon: BowlFood,
      title: "Nutrición Personalizada",
      description: "Recetas fitness, dietas adaptadas y hábitos que transforman tu físico.",
      tags: ["Recetas", "Dietas", "Macros"],
      span: "col-span-1",
      image: IMAGES.mealPrep,
      imagePosition: "object-center",
      imageOpacity: "opacity-70"
    },
    {
      icon: UsersFour,
      title: "Coaching Premium",
      description: "Acompañamiento real, contacto directo y seguimiento individualizado. Aquí no entrenas solo.",
      tags: ["1 a 1", "WhatsApp", "Mentoría"],
      span: "col-span-1 md:col-span-2",
      image: IMAGES.coaching,
      imagePosition: "object-[center_15%]", // Show face and shoulders
      imageOpacity: "opacity-55", // Darker for better text contrast
      highlight: true
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-24 bg-[#0A0A0A]" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight uppercase text-white mb-3 sm:mb-4">
            Todo lo que necesitas<br className="sm:hidden" /> para
            <span className="text-[#D4AF37]"> tu transformación</span>
          </h2>
          <p className="text-[#A3A3A3] text-base sm:text-lg max-w-2xl mx-auto">
            Un ecosistema completo diseñado para resultados reales.
          </p>
        </motion.div>

        {/* Bento Grid - Redesigned for Mobile */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6"
        >
          {benefits.map((benefit, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              whileHover={{ scale: 1.01, borderColor: "rgba(212, 175, 55, 0.5)" }}
              className={`${benefit.span} relative group overflow-hidden bg-[#141414] border border-white/10 transition-all duration-500 ${
                benefit.featured ? 'min-h-[320px] sm:min-h-[420px]' : benefit.highlight ? 'min-h-[280px] sm:min-h-[320px]' : 'min-h-[220px] sm:min-h-[280px]'
              } ${benefit.highlight ? 'border-[#D4AF37]/30' : ''}`}
            >
              {/* Background Image with enhanced visibility */}
              {benefit.image && (
                <div className="absolute inset-0 z-0">
                  <img 
                    src={benefit.image} 
                    alt={benefit.title}
                    className={`w-full h-full object-cover ${benefit.imagePosition || 'object-center'} ${benefit.imageOpacity || 'opacity-70'} group-hover:opacity-85 group-hover:scale-105 transition-all duration-700`}
                  />
                  {/* Gradient overlay - stronger for highlight cards */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-[#141414] ${benefit.highlight ? 'via-[#141414]/60' : 'via-[#141414]/40'} to-transparent`} />
                  <div className={`absolute inset-0 bg-gradient-to-r ${benefit.highlight ? 'from-[#141414]/50' : 'from-[#141414]/30'} to-transparent`} />
                </div>
              )}

              {/* Content */}
              <div className={`relative z-10 h-full flex flex-col ${benefit.image ? 'justify-end' : 'justify-between'} p-5 sm:p-6 md:p-8`}>
                {/* Icon */}
                <div className={`w-11 h-11 sm:w-12 sm:h-12 bg-[#D4AF37]/15 backdrop-blur-sm flex items-center justify-center mb-auto group-hover:bg-[#D4AF37]/25 transition-colors duration-300 ${benefit.image ? 'mb-4' : ''}`}>
                  <benefit.icon weight="duotone" className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" />
                </div>

                <div>
                  {/* Title */}
                  <h3 className="font-['Outfit'] text-lg sm:text-xl md:text-2xl font-bold tracking-tight text-white mb-2" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.9)' }}>
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-white/90 text-sm sm:text-base leading-relaxed mb-4" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
                    {benefit.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {benefit.tags.map((tag, tagIdx) => (
                      <span 
                        key={tagIdx}
                        className="px-2.5 py-1 text-[10px] sm:text-xs font-bold tracking-wider uppercase bg-black/40 backdrop-blur-sm border border-white/20 text-white/90"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Hover glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-transparent" />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============ MOTIVATIONAL SECTION ============
const MotivationalSection = () => {
  return (
    <section className="relative py-24 sm:py-32 md:py-40 overflow-hidden" data-testid="motivational-section">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img 
          src={IMAGES.motivational}
          alt="Athlete training"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/85" />
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center"
      >
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="font-['Outfit'] text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight uppercase leading-none text-white mb-4 sm:mb-6"
        >
          Empieza hoy.<br />
          <span className="text-[#D4AF37]">No mañana.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-lg sm:text-xl md:text-2xl text-[#A3A3A3] font-medium"
        >
          Tu cuerpo cambia cuando tu disciplina cambia.
        </motion.p>

        {/* Decorative line */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="w-16 sm:w-24 h-1 bg-[#D4AF37] mx-auto mt-8 sm:mt-10"
        />
      </motion.div>
    </section>
  );
};

// ============ FINAL CTA SECTION ============
const FinalCTASection = ({ onAccess }) => {
  return (
    <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-24 bg-[#0A0A0A]" data-testid="final-cta-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto text-center"
      >
        <h2 className="font-['Outfit'] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight uppercase text-white mb-4 sm:mb-6">
          ¿Listo para convertirte en<br />
          <span className="text-[#D4AF37]">tu mejor versión?</span>
        </h2>

        <p className="text-[#A3A3A3] text-base sm:text-lg mb-8 sm:mb-10 max-w-xl mx-auto">
          Únete a GOATGYM y empieza tu transformación hoy mismo.
          Sin excusas. Sin límites.
        </p>

        <motion.button
          onClick={onAccess}
          whileHover={{ 
            scale: 1.02, 
            boxShadow: "0 0 60px rgba(212, 175, 55, 0.5), inset 0 0 20px rgba(255,255,255,0.1)" 
          }}
          whileTap={{ scale: 0.98 }}
          className="group relative px-10 sm:px-14 py-4 sm:py-5 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-black font-bold text-sm sm:text-base tracking-[0.1em] uppercase overflow-hidden transition-all duration-500 inline-flex items-center gap-3"
          data-testid="final-cta-btn"
        >
          {/* Shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
          <span className="relative">Entrar en GOATGYM</span>
          <ArrowRight weight="bold" className="relative w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </motion.button>
      </motion.div>
    </section>
  );
};

// ============ FOOTER ============
// APP VERSION - for debugging cache issues
const APP_VERSION = "v2.4.0-onboarding-fix";

const Footer = () => {
  return (
    <footer className="py-6 sm:py-8 px-4 sm:px-6 border-t border-white/5 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-2">
          <span className="font-['Outfit'] text-lg sm:text-xl font-black tracking-tighter text-white">
            GOAT<span className="text-[#D4AF37]">GYM</span>
          </span>
        </div>
        <div className="flex flex-col items-center sm:items-end gap-1">
          <p className="text-[#666] text-xs sm:text-sm">
            © {new Date().getFullYear()} GOATGYM. Todos los derechos reservados.
          </p>
          <p className="text-[#444] text-[10px]" data-testid="app-version">
            {APP_VERSION}
          </p>
        </div>
      </div>
    </footer>
  );
};

// ============ MAIN COMPONENT ============
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleAccess = () => {
    if (user) {
      navigate(user.role === "admin" ? "/admin" : "/hoy");
    } else {
      navigate("/login");
    }
  };

  const handleWhatsApp = () => {
    const encodedMessage = encodeURIComponent(WHATSAPP_MESSAGE);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, "_blank");
  };

  return (
    <div 
      className="min-h-screen bg-[#0A0A0A] overflow-x-hidden"
      style={{ fontFamily: "'Manrope', sans-serif" }}
      data-testid="landing-page"
    >
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <Navbar onAccess={handleAccess} />
      <HeroSection onAccess={handleAccess} onWhatsApp={handleWhatsApp} />
      <BenefitsSection />
      <MotivationalSection />
      <FinalCTASection onAccess={handleAccess} />
      <Footer />
    </div>
  );
};

export default LandingPage;
